/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSyncAlt,
  faCoins,
  faTrash,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import RenewProductModal from "./RenewProductModal";
import UpdateCreditsModal from "./UpdateCreditsModal";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);

  useEffect(() => {
    fetchProductDetails();
  }, [id]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}settings/product-subscriptions/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.data?.success && response.data?.data) {
        setProduct(response.data.data);
      } else {
        showToast.error("Product not found");
        navigate("/research/ProductSubscriptions");
      }
    } catch (err) {
      console.error("Error fetching product details:", err);
      showToast.error("Failed to load product details.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${product?.product_name}" and its entire renewal history?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(`${API_URL}settings/product-subscriptions/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      showToast.success("Product deleted successfully");
      navigate("/research/ProductSubscriptions");
    } catch (err) {
      console.error("Error deleting product:", err);
      showToast.error("Failed to delete product.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return moment(date).format("DD-MM-YYYY");
    } catch {
      return "—";
    }
  };

  const renderStatusBadge = (status, isExpiringSoon) => {
    if (isExpiringSoon && status === "active") {
      return (
        <span
          className="badge badge-warning font-weight-normal"
          style={{ backgroundColor: "#f59c1a", color: "#fff" }}
        >
          Expiring Soon
        </span>
      );
    }
    switch (status) {
      case "active":
        return (
          <span
            className="badge badge-success font-weight-normal"
            style={{ backgroundColor: "#32a932", color: "#fff" }}
          >
            Active
          </span>
        );
      case "low_balance":
        return (
          <span
            className="badge badge-warning font-weight-normal"
            style={{ backgroundColor: "#f59c1a", color: "#fff" }}
          >
            Low Balance
          </span>
        );
      case "expired":
        return (
          <span
            className="badge badge-danger font-weight-normal"
            style={{ backgroundColor: "#ff5b57", color: "#fff" }}
          >
            Expired
          </span>
        );
      case "cancelled":
        return (
          <span
            className="badge font-weight-normal"
            style={{ backgroundColor: "#6c757d", color: "#fff" }}
          >
            Cancelled
          </span>
        );
      default:
        return (
          <span
            className="badge font-weight-normal"
            style={{ backgroundColor: "#6c757d", color: "#fff" }}
          >
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
        <div id="content" className="p-5 text-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!product) return null;

  const contact = product.contact_details || {};
  const companyInfo = contact.company_info || {};
  const contactInfo = contact.contact_info || {};
  const initPerson = contact.initial_contact_person || {};
  const suppPerson = contact.support_contact_person || {};
  const latestRenewal = product.latest_renewal;
  const renewals = product.renewals || [];

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Breadcrumb & Actions Bar */}
        <div className="panel-heading d-flex justify-content-between align-items-center mb-3">
          <div>
            <ul className="breadcrumb m-0" style={{ background: "transparent", padding: 0 }}>
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ textDecoration: "none" }}>
                  <FontAwesomeIcon icon={faHome} /> Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/research/ProductSubscriptions" style={{ textDecoration: "none" }}>
                  Product Subscriptions
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ fontWeight: 600 }}>
                {product.product_name}
              </li>
            </ul>
          </div>
          <div>
            <Link
              to="/research/ProductSubscriptions"
              className="btn btn-default btn-sm mx-1"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back
            </Link>
            {product.type === "credits" && (
              <button
                type="button"
                className="btn btn-default btn-sm mx-1"
                onClick={() => setIsCreditModalOpen(true)}
              >
                <FontAwesomeIcon icon={faCoins} className="text-warning me-1" /> Update Credits
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm mx-1"
              onClick={() => setIsRenewModalOpen(true)}
            >
              <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Renew / Recharge
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm mx-1"
              onClick={handleDeleteProduct}
              title="Delete Product"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>

        {/* Current Active Summary Panel */}
        {latestRenewal && (
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-heading bg-light border-bottom p-2 d-flex justify-content-between align-items-center">
              <h5 className="panel-title m-0 fw-bold" style={{ fontSize: "0.9rem" }}>
                {product.product_name}&nbsp;&nbsp;
                <span className="badge font-weight-normal" style={{ backgroundColor: "#348fe2", color: "#fff" }}>
                  {product.type === "subscription" ? "Subscription" : "Credits"}
                </span>
                &nbsp;&nbsp;
                {renderStatusBadge(product.status, product.is_expiring_soon)}
              </h5>
              <span className="text-muted" style={{ fontSize: "12px" }}>
                Current Active Cycle #{latestRenewal.renewal_number}
              </span>
            </div>
            <div className="panel-body p-3">
              <div className="row text-center g-3">
                <div className="col-md-3 col-6 border-end">
                  <small className="text-muted fw-bold d-block">START DATE</small>
                  <strong className="fs-6">{formatDate(latestRenewal.start_date)}</strong>
                </div>

                {product.type === "subscription" ? (
                  <>
                    <div className="col-md-3 col-6 border-end">
                      <small className="text-muted fw-bold d-block">EXPIRY DATE</small>
                      <strong className="fs-6 text-primary">{formatDate(latestRenewal.end_date)}</strong>
                    </div>
                    <div className="col-md-3 col-6 border-end">
                      <small className="text-muted fw-bold d-block">DAYS TO EXPIRY</small>
                      <strong
                        className="fs-6"
                        style={{
                          color:
                            product.days_until_expiry !== null && product.days_until_expiry < 0
                              ? "#ff5b57"
                              : product.is_expiring_soon
                              ? "#f59c1a"
                              : "#32a932",
                        }}
                      >
                        {product.days_until_expiry !== null
                          ? product.days_until_expiry < 0
                            ? `Expired (${Math.abs(product.days_until_expiry)} days ago)`
                            : `${product.days_until_expiry} days remaining`
                          : "Active"}
                      </strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-3 col-6 border-end">
                      <small className="text-muted fw-bold d-block">CREDITS REMAINING</small>
                      <strong
                        className="fs-6"
                        style={{
                          color: product.is_low_balance ? "#ff5b57" : "#32a932",
                        }}
                      >
                        {Number(latestRenewal.credits_remaining || 0).toLocaleString("en-IN")}
                      </strong>
                    </div>
                    <div className="col-md-3 col-6 border-end">
                      <small className="text-muted fw-bold d-block">LOW BALANCE ALERT AT</small>
                      <strong className="fs-6 text-muted">
                        &le; {Number(latestRenewal.low_balance_alert_at || 0).toLocaleString("en-IN")}
                      </strong>
                    </div>
                  </>
                )}

                <div className="col-md-3 col-6">
                  <small className="text-muted fw-bold d-block">CYCLE AMOUNT PAID</small>
                  <strong className="fs-6 text-dark">
                    ₹{Number(latestRenewal.amount_paid || 0).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company & Contact Details Panels */}
        <div className="row g-3 mb-3">
          {/* Company Info */}
          <div className="col-md-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100">
              <div className="panel-heading bg-light border-bottom p-2">
                <h5 className="panel-title m-0 fw-bold" style={{ fontSize: "0.85rem" }}>
                  Company Information
                </h5>
              </div>
              <div className="panel-body p-0">
                <table className="table table-bordered table-condensed m-0">
                  <tbody>
                    <tr>
                      <td style={{ width: "35%", fontWeight: 600, background: "#f8f9fa" }}>
                        Company Name
                      </td>
                      <td>{companyInfo.company_name || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Address</td>
                      <td>{companyInfo.address || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>GST Number</td>
                      <td>{companyInfo.gst_number || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Website</td>
                      <td>
                        {contactInfo.website ? (
                          <a
                            href={
                              contactInfo.website.startsWith("http")
                                ? contactInfo.website
                                : `https://${contactInfo.website}`
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {contactInfo.website}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Support URL</td>
                      <td>
                        {contactInfo.support_url ? (
                          <a
                            href={
                              contactInfo.support_url.startsWith("http")
                                ? contactInfo.support_url
                                : `https://${contactInfo.support_url}`
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            {contactInfo.support_url}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Initial Contact Person */}
          <div className="col-md-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100">
              <div className="panel-heading bg-light border-bottom p-2">
                <h5 className="panel-title m-0 fw-bold" style={{ fontSize: "0.85rem" }}>
                  Primary Contact Person
                </h5>
              </div>
              <div className="panel-body p-0">
                <table className="table table-bordered table-condensed m-0">
                  <tbody>
                    <tr>
                      <td style={{ width: "35%", fontWeight: 600, background: "#f8f9fa" }}>
                        Name
                      </td>
                      <td>{initPerson.name || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Designation</td>
                      <td>{initPerson.designation || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Email(s)</td>
                      <td>
                        {initPerson.emails && initPerson.emails.length > 0 ? (
                          initPerson.emails.map((e, idx) => (
                            <div key={idx}>
                              <a href={`mailto:${e}`}>{e}</a>
                            </div>
                          ))
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Phone(s)</td>
                      <td>
                        {initPerson.phones && initPerson.phones.length > 0 ? (
                          initPerson.phones.map((p, idx) => (
                            <div key={idx}>
                              <a href={`tel:${p}`}>{p}</a>
                            </div>
                          ))
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Support Contact Person */}
          <div className="col-md-4">
            <div className="panel panel-default with-rounded-corner with-shadow h-100">
              <div className="panel-heading bg-light border-bottom p-2">
                <h5 className="panel-title m-0 fw-bold" style={{ fontSize: "0.85rem" }}>
                  Support Contact Person
                </h5>
              </div>
              <div className="panel-body p-0">
                <table className="table table-bordered table-condensed m-0">
                  <tbody>
                    <tr>
                      <td style={{ width: "35%", fontWeight: 600, background: "#f8f9fa" }}>
                        Name
                      </td>
                      <td>{suppPerson.name || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Designation</td>
                      <td>{suppPerson.designation || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Email(s)</td>
                      <td>
                        {suppPerson.emails && suppPerson.emails.length > 0 ? (
                          suppPerson.emails.map((e, idx) => (
                            <div key={idx}>
                              <a href={`mailto:${e}`}>{e}</a>
                            </div>
                          ))
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 600, background: "#f8f9fa" }}>Phone(s)</td>
                      <td>
                        {suppPerson.phones && suppPerson.phones.length > 0 ? (
                          suppPerson.phones.map((p, idx) => (
                            <div key={idx}>
                              <a href={`tel:${p}`}>{p}</a>
                            </div>
                          ))
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Notes if any */}
        {product.notes && (
          <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: "0.85rem" }}>
            <strong>Notes:</strong> {product.notes}
          </div>
        )}

        {/* Renewal History Panel */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading bg-light border-bottom p-2 d-flex justify-content-between align-items-center">
            <h5 className="panel-title m-0 fw-bold" style={{ fontSize: "0.9rem" }}>
              Renewal & Recharge History ({renewals.length} cycle(s))
            </h5>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-condensed table-hover table-bordered bg-white sticky-table m-0">
                <thead>
                  <tr>
                    <th style={{ width: "70px", textAlign: "center" }}>Cycle #</th>
                    <th>Start Date</th>
                    <th>
                      {product.type === "subscription" ? "Expiry Date" : "Credits Added"}
                    </th>
                    <th>
                      {product.type === "subscription"
                        ? "Alert Days"
                        : "Remaining / Threshold"}
                    </th>
                    <th className="ctc">Amount Paid</th>
                    <th>Remarks / Notes</th>
                    <th className="ctc">Recorded On</th>
                  </tr>
                </thead>
                <tbody>
                  {renewals.map((r, idx) => {
                    const isActive = idx === 0;
                    return (
                      <tr key={r.id}>
                        <td className="ctc">
                          <strong>#{r.renewal_number}</strong>
                          {isActive && (
                            <span
                              className="badge badge-success ms-1"
                              style={{
                                backgroundColor: "#32a932",
                                color: "#fff",
                                marginLeft: "5px",
                              }}
                            >
                              Active
                            </span>
                          )}
                        </td>
                        <td>{formatDate(r.start_date)}</td>
                        <td>
                          {product.type === "subscription" ? (
                            <strong>{formatDate(r.end_date)}</strong>
                          ) : (
                            <strong>
                              {Number(r.total_credits || 0).toLocaleString("en-IN")}
                            </strong>
                          )}
                        </td>
                        <td>
                          {product.type === "subscription" ? (
                            <span>{r.reminder_before_days || 15} days</span>
                          ) : (
                            <span>
                              <strong>
                                {Number(r.credits_remaining || 0).toLocaleString("en-IN")}
                              </strong>{" "}
                              / {Number(r.low_balance_alert_at || 0).toLocaleString("en-IN")}
                            </span>
                          )}
                        </td>
                        <td className="ctc">
                          <strong>₹{Number(r.amount_paid || 0).toLocaleString("en-IN")}</strong>
                        </td>
                        <td>{r.notes || "—"}</td>
                        <td className="ctc text-muted" style={{ fontSize: "11px" }}>
                          {formatDate(r.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <RenewProductModal
        isOpen={isRenewModalOpen}
        product={product}
        onClose={() => setIsRenewModalOpen(false)}
        onSuccess={(updated) => setProduct(updated)}
      />

      <UpdateCreditsModal
        isOpen={isCreditModalOpen}
        product={product}
        onClose={() => setIsCreditModalOpen(false)}
        onSuccess={(updated) => setProduct(updated)}
      />

      {/* Scoped Report Styles */}
      <style>{`
        .table-condensed th,
        .table-condensed td {
          padding: 8px 5px;
          font-size: 12px;
        }

        .btn-default {
          background: #f8f9fa;
          border: 1px solid #ddd;
        }

        .btn-xs {
          padding: 1px 5px;
          font-size: 12px;
          line-height: 1.5;
        }

        th {
          white-space: nowrap;
          background: #f8f9fa;
        }

        .table-hover tbody tr:hover {
          background-color: #f5f5f5;
        }

        .panel-heading {
          padding: 10px 15px;
          border-bottom: 1px solid #eee;
          background: #f8f9fa;
        }

        .ctc {
          text-align: center;
        }

        .badge {
          font-size: 11px;
          padding: 3px 6px;
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
